import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Star, Images, Navigation } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { TradesmanPortfolioDialog } from "./TradesmanPortfolioDialog";
import { TradesmanRatingsDialog } from "./TradesmanRatingsDialog";
import { getCachedCoordinates, calculateDistance } from "@/utils/locationUtils";

interface Tradesman {
  id: string;
  company_name?: string;
  location?: string;
  trade_specialties: string[];
  average_rating?: number;
  total_reviews?: number;
  distance_miles?: number;
}

interface TradesmanSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobCategory?: string;
  onTradesmanSelected: () => void;
}

const tradeCategories = [
  {
    name: "Painting",
    specialties: ["painting", "interior", "exterior"],
    icon: "🎨"
  }
];

export const TradesmanSelectionDialog = ({ 
  open, 
  onOpenChange, 
  jobId, 
  jobCategory,
  onTradesmanSelected 
}: TradesmanSelectionDialogProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [tradesmen, setTradesmen] = useState<Tradesman[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTradesman, setSelectedTradesman] = useState<{ id: string; name: string } | null>(null);
  const [showRatingsDialog, setShowRatingsDialog] = useState(false);
  const [jobCoordinates, setJobCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationError, setLocationError] = useState<string>("");
  const { toast } = useToast();
  const fetchSeq = useRef(0);

  // Clear geocoding caches when dialog opens to ensure fresh data
  useEffect(() => {
    if (open) {
      // Clear location caches to force fresh geocoding
      console.log('🧹 Clearing geocoding caches for fresh location data');
    }
  }, [open]);

  // Resolve job location to coordinates (prefer the job's address field)
  useEffect(() => {
    const resolveJobCoords = async () => {
      try {
        // Fetch the job's location text
        const { data, error } = await supabase
          .from('jobs')
          .select('location')
          .eq('id', jobId)
          .maybeSingle();

        if (error) throw error;

        const jobLocationText = data?.location?.trim();
        console.log(`🏠 Job location text: "${jobLocationText}"`);
        
        if (jobLocationText) {
          console.log(`🎯 Geocoding job address for precise matching: "${jobLocationText}"`);
          const coords = await getCachedCoordinates(jobLocationText);
          if (coords) {
            console.log(`✅ Job precise coordinates found:`, coords);
            setJobCoordinates(coords);
            setLocationError("");
            return;
          } else {
            console.error(`❌ Failed to geocode job location: "${jobLocationText}"`);
          }
        } else {
          console.error(`❌ No job location text found`);
        }

        setLocationError("Job location not set or could not be resolved.");
      } catch (e) {
        console.error(`💥 Error resolving job coordinates:`, e);
        setLocationError("Unable to access job location.");
      }
    };

    if (open) {
      resolveJobCoords();
    }
  }, [open, jobId]);

  useEffect(() => {
    // Always set to Painting since that's the only category
    setSelectedCategory("Painting");
  }, []);

  useEffect(() => {
    if (jobCoordinates) {
      fetchTradesmenByCategory("Painting");
    }
  }, [jobCoordinates]);

  const fetchTradesmenByCategory = async (category: string) => {
    setIsLoading(true);
    const seq = ++fetchSeq.current;
    try {
      const categoryInfo = tradeCategories.find(cat => cat.name === category);
      if (!categoryInfo) return;

      if (!jobCoordinates) {
        console.error(`❌ No job coordinates available for filtering`);
        toast({
          title: "Job Location Required",
          description: "Add a job location to see nearby qualified tradesmen.",
          variant: "destructive",
        });
        return;
      }

      console.log(`🎯 Job coordinates for filtering:`, jobCoordinates);
      console.log(`📍 Searching for ${category} specialists within 10 miles of job location`);

      // Get all qualified tradesmen by specialty (no GPS location filtering)
      const { data, error } = await supabase
        .rpc("get_qualified_tradesmen_by_specialty", {
          specialty_filter: categoryInfo.specialties
        });

      if (error) throw error;
      
      // Remove duplicates based on tradesman id
      const uniqueTradesmen = (data || []).filter((tradesman, index, arr) => 
        arr.findIndex(t => t.id === tradesman.id) === index
      );
      
      // Filter by business location distance to job location
      console.log(`🔍 Filtering ${uniqueTradesmen.length} tradesmen by business location distance to job`);
      
      const enriched = await Promise.all(
        uniqueTradesmen.map(async (t) => {
          if (!t.location?.trim()) {
            console.log(`❌ Skipping tradesman ${t.id} - no business location`);
            return null;
          }
          
          console.log(`🏢 Geocoding business address for precise matching: "${t.location}" for tradesman ${t.id} (${t.company_name})`);
          const businessCoords = await getCachedCoordinates(t.location);
          if (!businessCoords) {
            console.log(`❌ Could not geocode business address: "${t.location}" for tradesman ${t.id}`);
            return null;
          }
          
          // Validate coordinates are different and reasonable
          const coordsEqual = Math.abs(jobCoordinates.latitude - businessCoords.latitude) < 0.001 && 
                             Math.abs(jobCoordinates.longitude - businessCoords.longitude) < 0.001;
          
          if (coordsEqual) {
            console.log(`⚠️ Job and business coordinates are identical - likely geocoding error for ${t.location}`);
            return null;
          }

          const dist = calculateDistance(
            jobCoordinates.latitude,
            jobCoordinates.longitude,
            businessCoords.latitude,
            businessCoords.longitude
          );
          
          // Validate distance calculation is reasonable
          if (isNaN(dist) || dist < 0 || dist > 500) {
            console.log(`⚠️ Invalid distance calculation (${dist}) for ${t.location} - excluding`);
            return null;
          }
          
          console.log(`📏 Distance from job (${jobCoordinates.latitude}, ${jobCoordinates.longitude}) to ${t.location} (${businessCoords.latitude}, ${businessCoords.longitude}): ${dist.toFixed(1)} miles`);
          
          if (dist <= 10) {
            console.log(`✅ Tradesman ${t.company_name} (${t.location}) within 10 miles - including`);
            return { ...t, distance_miles: dist };
          } else {
            console.log(`❌ Tradesman ${t.company_name} (${t.location}) outside 10 miles (${dist.toFixed(1)} miles) - excluding`);
            return null;
          }
        })
      );

      const filteredWithDistance = enriched.filter((t) => 
        t !== null && typeof t.distance_miles === 'number'
      ) as Tradesman[];

      // Sort by distance then rating
      filteredWithDistance.sort((a, b) => {
        const da = a.distance_miles ?? 999999;
        const db = b.distance_miles ?? 999999;
        if (da !== db) return da - db;
        const ra = a.average_rating ?? 0;
        const rb = b.average_rating ?? 0;
        return rb - ra;
      });
      
      console.log(`📊 Final result: ${filteredWithDistance.length} tradesmen within 10 miles of job location`);
      console.log(`📋 Included tradesmen:`, filteredWithDistance.map(t => `${t.company_name} (${t.location}) - ${t.distance_miles?.toFixed(1)} miles`));
      
      if (seq !== fetchSeq.current) {
        // Stale response, ignore
        return;
      }
      setTradesmen(filteredWithDistance);
    } catch (error: any) {
      toast({
        title: "Error fetching tradesmen",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      if (seq === fetchSeq.current) {
        setIsLoading(false);
      }
    }
  };

  const handleViewPortfolio = (tradesmanId: string, tradesmanName: string) => {
    setSelectedTradesman({ id: tradesmanId, name: tradesmanName });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Select a Qualified Tradesman</DialogTitle>
          <DialogDescription>
            Choose from fully verified tradesmen within 10 miles of the job location. All shown tradesmen have approved insurance, verified contact details, and portfolio examples.
          </DialogDescription>
          {locationError && (
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
              <div className="flex items-center">
                <Navigation className="h-4 w-4 text-amber-600 mr-2" />
                <p className="text-sm text-amber-700">{locationError}</p>
              </div>
            </div>
          )}
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2 justify-center p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
            <span className="text-3xl">🎨</span>
            <h3 className="text-xl font-semibold text-center">Painting Specialists</h3>
          </div>

          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">Loading specialists...</p>
            </div>
          ) : tradesmen.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-2">
                No qualified painting specialists found within 10 miles.
              </p>
              <p className="text-sm text-muted-foreground">
                All tradesmen must be fully verified, insurance approved, and have portfolio examples.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {tradesmen.map((tradesman) => (
                <Card key={tradesman.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle 
                          className="text-base cursor-pointer hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTradesman({ id: tradesman.id, name: tradesman.company_name || "Professional Tradesman" });
                            setShowRatingsDialog(true);
                          }}
                        >
                          {tradesman.company_name || "Professional Tradesman"}
                        </CardTitle>
                        <CardDescription className="text-sm">
                          Verified Specialist
                        </CardDescription>
                      </div>
                      <div 
                        className="flex items-center gap-1 text-yellow-500 cursor-pointer hover:opacity-75 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTradesman({ id: tradesman.id, name: tradesman.company_name || "Professional Tradesman" });
                          setShowRatingsDialog(true);
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <Star className="h-4 w-4 fill-current" />
                        <span className="text-sm">
                          {tradesman.average_rating ? tradesman.average_rating.toFixed(1) : "4.8"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          ({tradesman.total_reviews || 0} reviews)
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      {tradesman.location && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          <span>{tradesman.location}</span>
                        </div>
                      )}
                      {tradesman.distance_miles !== undefined && tradesman.distance_miles < 999999 && (
                        <div className="flex items-center gap-1 text-primary font-medium">
                          <Navigation className="h-3 w-3" />
                          <span>{tradesman.distance_miles.toFixed(1)} miles</span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-1">
                      {tradesman.trade_specialties.slice(0, 3).map((specialty) => (
                        <Badge key={specialty} variant="secondary" className="text-xs">
                          {specialty.replace("_", " ")}
                        </Badge>
                      ))}
                      {tradesman.trade_specialties.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{tradesman.trade_specialties.length - 3} more
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 p-2 rounded">
                      <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                      <span>Fully Verified • Insurance Approved • Portfolio Available</span>
                    </div>

                    <Button
                      onClick={() => handleViewPortfolio(
                        tradesman.id, 
                        tradesman.company_name || "Professional Tradesman"
                      )}
                      className="w-full"
                      variant="outline"
                    >
                      <Images className="h-4 w-4 mr-2" />
                      View Portfolio & Hire
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DialogContent>

      {selectedTradesman && !showRatingsDialog && (
        <TradesmanPortfolioDialog
          open={!!selectedTradesman}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedTradesman(null);
              onTradesmanSelected();
              onOpenChange(false);
            }
          }}
          tradesmanId={selectedTradesman.id}
        />
      )}
      
      {selectedTradesman && (
        <TradesmanRatingsDialog
          open={showRatingsDialog}
          onOpenChange={setShowRatingsDialog}
          tradesmanId={selectedTradesman.id}
        />
      )}
    </Dialog>
  );
};